import { computed, Injectable, InjectionToken, Signal, signal, WritableSignal, inject } from '@angular/core';
import { forkJoin, map, Observable, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

import { IfFeatureService } from '../if-feature/if-feature.service';
import { Address } from '../../domain/address.domain';
import {
  Employment,
  KycUpdates,
  ContactEmailAddress,
  Kyc,
  CompanyDetails,
  CompanyCd,
  UpdateType,
  TelephoneAddress,
  PhoneUsageCd,
  ContactStreetAddress,
  ContactTelephoneAddress,
  StreetAddress,
  AddressUsageCd,
  ContactStreetAddressUsageType
} from '../../domain/user-profile.domain';
import { Industry, Occupation } from '../../domain/employment.domain';
import { PhonePipe } from '../../pipes/phone.pipe';
import { UserProfileUtilsService } from '../user-profile/user-profile-utils.service';
import { WiscService } from '../wisc.service';
import { RestService } from '../rest/rest.service';
import { ModalService } from '../../components/modal/modal.service';
import { UserProfileUpdateService } from '../user-profile/user-profile-update.service';
import {
  GamlAttestationFlowType,
  GamlEmployment,
  GamlProfile,
  GamlStatusData,
  GamlConfiguration,
  GamlAttestationResponse,
  GamlAttestationRequest,
  GamlAttestationParams
} from './gaml-attestation.domain';

export const GAML_CONFIGURATION = new InjectionToken<boolean>('GamlConfiguration');

@Injectable({
  providedIn: 'root'
})
export class GamlAttestationService {
  private readonly gamlConfiguration = inject<GamlConfiguration>(GAML_CONFIGURATION, { optional: true });
  private readonly restService = inject(RestService);
  private readonly userProfileUtilsService = inject(UserProfileUtilsService);
  private readonly phonePipe = inject(PhonePipe);
  private readonly wiscService = inject(WiscService);
  private readonly modalService = inject(ModalService);
  private readonly userProfileUpdateService = inject(UserProfileUpdateService);
  private readonly featureService = inject(IfFeatureService);

  private kycUpdates: KycUpdates = {};

  gamlTriggered: WritableSignal<boolean | undefined> = signal(undefined);
  gamlHaltTrade: WritableSignal<boolean | undefined> = signal(undefined);
  gamlTriggered$: Observable<boolean>;
  gamlHaltTrade$: Observable<boolean>;
  attestationNotDue: Signal<boolean> = computed(() => this.gamlTriggered() === undefined && this.gamlHaltTrade() === undefined);
  attestationDue: Signal<boolean> = computed(() => this.gamlTriggered() === true || this.gamlHaltTrade() === true);
  attestationComplete: Signal<boolean> = computed(() => this.gamlTriggered() === false && this.gamlHaltTrade() === false);
  gamlStatusData: GamlStatusData;
  gamlProfile: GamlProfile;
  hasEditClicked: boolean = false;
  flowType: GamlAttestationFlowType;
  gamlWFLFeature: boolean = false;
  gamlMVP1FixFeature: boolean = false;
  kycEnhancementsEnabled: boolean = false;

  constructor() {
    this.gamlTriggered$ = toObservable(this.gamlTriggered);
    this.gamlHaltTrade$ = toObservable(this.gamlHaltTrade);
    this.gamlWFLFeature = Boolean(this.gamlConfiguration?.gamlWFLFeature);
    this.gamlMVP1FixFeature = Boolean(this.gamlConfiguration?.gamlMVP1FixFeature);
    this.gamlTriggered.set(this.gamlConfiguration?.gamlTriggered);
    this.gamlHaltTrade.set(this.gamlConfiguration?.gamlHaltTrade);
    this.kycEnhancementsEnabled = this.featureService.getFeatureFlag<boolean>('kycEnhancementsEnabled');
  }

  getProfile(): Observable<GamlProfile> {
    return forkJoin({
      profile: this.restService.getResource('gaml.profileData').get<GamlProfile>(),
      industries: this.wiscService.getJsonData<Array<Industry>>('industries')
    }).pipe(
      map(({ profile, industries }: { profile: GamlProfile; industries: Array<Industry> }) => {
        const mergedProfile: GamlProfile = this.mergeProfile(profile, this.kycUpdates);

        return {
          ...mergedProfile,
          missingInfo: UserProfileUtilsService.populateMissingInfo(mergedProfile, this.kycEnhancementsEnabled),
          legalName: this.getLegalName(profile),
          residenceAddressLines: this.getAddressLines(mergedProfile.contactStreetAddresses, 'HOME_ADDRESS'),
          mailingAddressLines: this.getAddressLines(mergedProfile.contactStreetAddresses, 'MAILING_ADDRESS'),
          residencePhoneNumber: this.getPhoneNumber(mergedProfile.contactTelephoneAddresses, PhoneUsageCd.HOME),
          mobilePhoneNumber: this.getPhoneNumber(mergedProfile.contactTelephoneAddresses, PhoneUsageCd.MOBILE),
          employmentAddressLines: this.getEmploymentAddressLines(mergedProfile.employment),
          employmentDetails: this.getEmploymentDetails(mergedProfile.employment, industries),
          canadianAddress: this.getCanadianAddress(profile.contactStreetAddresses), // need to pass original profile here because the value is used for KYC
          industries
        }
      }),
      tap((profile: GamlProfile) => this.gamlProfile = profile)
    );
  }

  getStatusData(): Observable<GamlStatusData> {
    return this.restService
      .getResource('gaml.statusData')
      .get<GamlStatusData>()
      .pipe(
        tap((data: GamlStatusData) => {
          this.gamlStatusData = data;
        })
      );
  }

  postAttestation(params: GamlAttestationParams): Observable<GamlAttestationResponse> {
    const updates = this.userProfileUpdateService.getUpdates();
    const drafts: KycUpdates = {};
    updates.forEach((update: UpdateType) => {
      switch (update) {
        case UpdateType.ADDRESS:
          drafts.streetAddresses = this.kycUpdates.streetAddresses;
          break;
        case UpdateType.PHONE:
          drafts.telephoneAddresses = this.kycUpdates.telephoneAddresses;
          break;
        case UpdateType.EMAIL:
          drafts.emailAddresses = this.kycUpdates.emailAddresses;
          break;
        case UpdateType.EMPLOYMENT:
          drafts.employment = this.kycUpdates.employment;
          break;
        case UpdateType.PREFERENCE:
          drafts.preference = this.kycUpdates.preference;
          break;
      }
    });

    return this.restService
      .getResource('gaml.attestation')
      .post<GamlAttestationRequest, GamlAttestationResponse>({
        partyGroupList: this.gamlProfile.partyGroupResponseList,
        updateType: UpdateType.ATTESTATION,
        drafts,
        isAttestationComplete: params.isAttestationComplete
      });
  }

  setKycUpdates(updates: KycUpdates): void {
    if(typeof updates === 'object') {
      this.kycUpdates = {
        ...this.kycUpdates,
        ...updates
      }
    }
  }

  hasCachedKycUpdates(): boolean {
    return Object.keys(this.kycUpdates).length > 0;
  }

  getModalTitleId(): string {
    const modalId = this.modalService.getTopModal()?.id;
    return `${modalId}-title`;
  }

  cleanUpKycUpdates(): void {
    this.kycUpdates = {};
  }

  private getLegalName(profile: GamlProfile): string {
    const legalName: Array<string> = [];
    legalName.push(profile.givenName ?? '');
    legalName.push(profile.middleName ?? '');
    legalName.push(profile.familyName ?? '');
    return legalName.join(' ');
  }

  private formatAddress(address: Address | undefined): Array<string> {
    return address ? this.userProfileUtilsService.formatAddress(address) : [];
  }

  private mergeProfile(userProfile: GamlProfile, kycUpdates: KycUpdates): GamlProfile {
    let newStreetAddresses: Array<ContactStreetAddress> = userProfile.contactStreetAddresses;
    let newTelephoneAddresses: Array<ContactTelephoneAddress> = userProfile.contactTelephoneAddresses;
    let newEmployment: Employment = userProfile.employment;
    let newKyc: Kyc = userProfile.kyc;
    let newEmailAddress: ContactEmailAddress = userProfile.contactEmailAddress;

    if(Array.isArray(kycUpdates.streetAddresses) && kycUpdates.streetAddresses.length > 0) {
      newStreetAddresses = kycUpdates.streetAddresses.map(
        (address: StreetAddress) => {
          return {
            address: address.address,
            usageCd: address.usageCd === AddressUsageCd.PRIMARY_RESIDENCE ? 'HOME_ADDRESS' : 'MAILING_ADDRESS'
          }
        }
      );
    }

    if (Array.isArray(kycUpdates.telephoneAddresses) && kycUpdates.telephoneAddresses.length > 0) {
      newTelephoneAddresses = kycUpdates.telephoneAddresses.map(
        (telephoneAddress: TelephoneAddress) => {
          const phoneNumberString = telephoneAddress.telephone.telephoneNum;
          // KYC phone update is Canadian format only
          return {
            telephone: {
              countryCodeNum: '1',
              areaCodeNum: phoneNumberString.substring(0, 3),
              localNum: phoneNumberString.substring(3, phoneNumberString.length)
            },
            usageCd: telephoneAddress.usageCd === 'HOME_PHONE' ? PhoneUsageCd.HOME : PhoneUsageCd.MOBILE
          }
        }
      );
    }

    if(Array.isArray(kycUpdates.emailAddresses) && kycUpdates.emailAddresses.length > 0) {
      newEmailAddress = {
        email: {
          emailTxt: kycUpdates.emailAddresses[0].email?.emailTxt
        }
      }
    }

    if(kycUpdates.employment) {
      newEmployment = {
        employerName: kycUpdates.employment.employerName,
        occupationTypeCd: kycUpdates.employment.occupationTypeCd,
        streetAddress: kycUpdates.employment.streetAddress,
        statusCd: kycUpdates.employment.statusCd
      }
    }

    if(Array.isArray(kycUpdates.kyc?.company) && kycUpdates.kyc.company.length > 0) {
      newKyc = {
        pro: false,
        control: Boolean(kycUpdates.kyc.company.find((company: CompanyDetails) => company.companyCd === CompanyCd.COMPANY_CONTROL)),
        insider: Boolean(kycUpdates.kyc.company.find((company: CompanyDetails) => company.companyCd === CompanyCd.COMPANY_OFFICER))
      }
    }

    return {
      ...userProfile,
      contactStreetAddresses: newStreetAddresses,
      contactTelephoneAddresses: newTelephoneAddresses,
      contactEmailAddress: newEmailAddress,
      employment: newEmployment,
      kyc: newKyc
    }
  }

  private getAddressLines(contactStreetAddresses: Array<ContactStreetAddress>, addressUsage: ContactStreetAddressUsageType): Array<string> {
    let addressLines: Array<string> = [];

    if(Array.isArray(contactStreetAddresses) && contactStreetAddresses.length > 0) {
      const addressByUsage = contactStreetAddresses.find(
        (contactAddress: ContactStreetAddress) => contactAddress.usageCd === addressUsage
      );
      addressLines = addressByUsage ? this.formatAddress(addressByUsage.address) : [];
    }

    return addressLines;
  }

  private getPhoneNumber(contactTelephoneAddresses: Array<ContactTelephoneAddress>, phoneUsage: PhoneUsageCd): string {
    let formattedPhoneNumber = '';

    if(Array.isArray(contactTelephoneAddresses) && contactTelephoneAddresses.length > 0) {
      const contactPhone = contactTelephoneAddresses.find(
        (contactPhone: ContactTelephoneAddress) => contactPhone.usageCd === phoneUsage
      );
      const phoneNumber = [
        contactPhone?.telephone?.countryCodeNum,
        contactPhone?.telephone?.areaCodeNum,
        contactPhone?.telephone?.localNum
      ].join('');
      formattedPhoneNumber = this.phonePipe.transform(phoneNumber);
    }

    return formattedPhoneNumber;
  }

  private getEmploymentAddressLines(employment: Employment): Array<string> {
    const employmentAddress: Address | undefined = employment?.streetAddress;
    let employmentAddressLines: Array<string> = [];

    if(employmentAddress) {
      employmentAddressLines = this.formatAddress(employmentAddress);
    }

    return employmentAddressLines;
  }

  private getEmploymentDetails(employment: Employment, industries: Array<Industry>): GamlEmployment {
    const occupationId = employment.occupationTypeCd;
    let occupationDesc: string | undefined;
    let industry: Industry | undefined;

    if(occupationId) {
      for (const anIndustry of industries) {
        const occupations = anIndustry.occupations ?? [];
        const occupation = occupations.find((anOccupation: Occupation) => {
          // KYC Employment updates POST occupation code instead of occupation id as occupationTypeCd
          return anOccupation.id === occupationId || anOccupation.code === occupationId;
        });

        if(occupation) {
          occupationDesc = occupation.description;
          industry = anIndustry;
          break;
        }
      }
    }

    return {
      ...employment,
      occupationDesc,
      industryDesc: industry?.description
    };
  }

  private getCanadianAddress(contactStreetAddresses: Array<ContactStreetAddress>) : string {
    const homeAddress: Address | undefined = contactStreetAddresses.find(
      (anAddress: ContactStreetAddress) => anAddress.usageCd !== 'MAILING_ADDRESS')?.address;
    const mailingAddress: Address | undefined  = contactStreetAddresses.find(
      (anAddress: ContactStreetAddress) => anAddress.usageCd === 'MAILING_ADDRESS')?.address;

    if ((homeAddress && !UserProfileUtilsService.isCanadianAddress(homeAddress)
      || (mailingAddress && !UserProfileUtilsService.isCanadianAddress(mailingAddress)))) {
      return 'false';
    } else {
      return 'true';
    }
  }
}
